import PropTypes from 'prop-types';
import React from 'react';
import cx from 'classnames';
import { scaleBand, scaleLinear } from 'd3-scale';
import { max } from 'd3-array';
import { range } from 'lodash';
import GroupedBarChartXaxis from "./grouped-bar-chart-x-axis";
import GroupedBarChartYaxis from "./grouped-bar-chart-y-axis";

var GroupedBarChart = function GroupedBarChart(_ref) {
  var className = _ref.className,
      data = _ref.data,
      children = _ref.children,
      maxYValue = _ref.maxYValue,
      yTicks = _ref.yTicks,
      yTicksFormat = _ref.yTicksFormat,
      width = _ref.width,
      height = _ref.height,
      xOffset = _ref.xOffset,
      yOffset = _ref.yOffset,
      valuesFormat = _ref.valuesFormat,
      innerPadding = _ref.innerPadding;
  var x0 = scaleBand().rangeRound([xOffset, width]);
  var x1 = scaleBand().padding(innerPadding);
  var y = scaleLinear().rangeRound([height - yOffset, yOffset]);
  var labels = data.map(function (_ref2) {
    var key = _ref2.key;
    return key;
  });
  var keys = data.length ? range(data[0].values.length + 1) : [];
  x0.domain(labels);
  x1.domain(keys).rangeRound([0, x0.bandwidth()]);
  var maxY = typeof maxYValue === 'undefined' ? max(data.map(function (_ref3) {
    var values = _ref3.values;
    return values;
  }).reduce(function (acc, current) {
    return acc.concat(current);
  }, [])) : maxYValue;
  y.domain([0, maxY]).nice();
  var barWidth = x1.bandwidth();
  return React.createElement("div", {
    className: cx('GroupedBarChart', className)
  }, React.createElement("svg", {
    width: width,
    height: height
  }, children, React.createElement(GroupedBarChartYaxis, {
    ticks: yTicks,
    ticksFormat: yTicksFormat,
    scale: y,
    width: width,
    height: height,
    xOffset: xOffset,
    yOffset: yOffset
  }), data.map(function (_ref4) {
    var key = _ref4.key,
        values = _ref4.values;
    return React.createElement("g", {
      key: key,
      transform: "translate(".concat(x0(key), ", 0)")
    }, values.map(function (value, idx) {
      return React.createElement("g", {
        key: idx,
        className: "GroupedBarChart-bar-holder"
      }, React.createElement("rect", {
        x: x1(idx),
        y: y(value),
        width: barWidth,
        height: height - yOffset - y(value),
        className: "GroupedBarChart-bar-".concat(key, "\n                                                 GroupedBarChart-bar-i").concat(idx)
      }), React.createElement("text", {
        x: x1(idx) + barWidth / 2,
        y: y(value),
        className: cx('GroupedBarChart-bar-value', {
          'GroupedBarChart-bar-value-last': idx > 0
        })
      }, valuesFormat(value)));
    }), React.createElement("text", {
      y: height,
      x: barWidth * values.length / 2,
      className: "GroupedBarChartXaxis-label"
    }, key));
  }), React.createElement(GroupedBarChartXaxis, {
    width: width,
    height: height,
    xOffset: xOffset,
    yOffset: yOffset
  })));
};

GroupedBarChart.displayName = 'GroupedBarChart';
GroupedBarChart.propTypes = {
  /**
   * className, which applies on root node of component.
   */
  className: PropTypes.string,

  /**
   * Input data for component.
   */
  data: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.any.isRequired,
    values: PropTypes.array.isRequired
  })).isRequired,

  /**
   * Additional content, can be useful for customization.
   */
  children: PropTypes.any,

  /**
   * If specified sets top border of values.
   */
  maxYValue: PropTypes.number,

  /**
   * Array of y axis ticks.
   */
  yTicks: PropTypes.arrayOf(PropTypes.number).isRequired,

  /**
   * Function, which formats existing `yTicks` array.
   */
  yTicksFormat: PropTypes.func,

  /**
   * Width of component.
   */
  width: PropTypes.number.isRequired,

  /**
   * Height of component.
   */
  height: PropTypes.number.isRequired,

  /**
   * Offset from left by x axis for displaying bars.
   */
  xOffset: PropTypes.number,

  /**
   * Offset from top by y axis for displaying bars.
   */
  yOffset: PropTypes.number,

  /**
   * Function, which formats labels on top of each bar.
   */
  valuesFormat: PropTypes.func,

  /**
   * Defined inner padding between bars
   */
  innerPadding: PropTypes.number
};
GroupedBarChart.defaultProps = {
  valuesFormat: function valuesFormat(value) {
    return value;
  },
  yTicksFormat: function yTicksFormat(value) {
    return value;
  },
  xOffset: 0,
  yOffset: 0,
  innerPadding: 0
};
export default GroupedBarChart;